'use client';

import React from 'react';
import {
  UseFormRegister,
  RegisterOptions,
  FieldValues,
  Path,
} from 'react-hook-form';
import { cn } from '@/lib/utils';

interface FormInputProps<T extends FieldValues>
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'name'> {
  label?: string;
  error?: string;
  helperText?: string;
  register?: UseFormRegister<T>;
  name: Path<T>;
  rules?: RegisterOptions<T>;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export function FormInput<T extends FieldValues>({
  label,
  error,
  helperText,
  register,
  name,
  rules,
  type = 'text',
  className,
  leftIcon,
  rightIcon,
  ...props
}: FormInputProps<T>) {
  const isRequired = !!rules?.required;

  // Transform value for number inputs
  const registerOptions = {
    ...rules,
    ...(type === 'number' && {
      setValueAs: (value: string) => (value === '' ? undefined : Number(value)),
    }),
  };

  // Get appropriate autocomplete value based on field name and type
  const getAutocomplete = () => {
    if (props.autoComplete) return props.autoComplete;

    const nameStr = String(name).toLowerCase();

    if (type === 'email' || nameStr.includes('email')) return 'email';
    if (
      type === 'tel' ||
      nameStr.includes('phone') ||
      nameStr.includes('telephone')
    )
      return 'tel';
    if (nameStr.includes('name')) return 'name';
    if (nameStr.includes('password')) return 'current-password';
    if (nameStr.includes('username')) return 'username';
    if (nameStr.includes('address')) return 'address-line1';
    if (nameStr.includes('city')) return 'address-level2';
    if (nameStr.includes('country')) return 'country';
    if (nameStr.includes('postal') || nameStr.includes('zip'))
      return 'postal-code';
    if (nameStr.includes('organization') || nameStr.includes('company'))
      return 'organization';
    if (nameStr.includes('title') || nameStr.includes('job'))
      return 'organization-title';

    return 'off';
  };

  return (
    <div className="w-full space-y-1">
      {/* Label */}
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-gray-700"
        >
          {label} {isRequired && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* Input Field */}
      <div className="relative">
        {leftIcon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-3">
            {leftIcon}
          </div>
        )}
        <input
          id={name}
          type={type}
          autoComplete={getAutocomplete()}
          aria-required={isRequired}
          {...(register ? register(name, registerOptions) : {})}
          className={cn(
            'block w-full rounded-lg border border-gray-300 shadow-sm sm:text-sm',
            'focus:border-primary-500 focus:ring-primary-500',
            leftIcon && 'pl-10',
            rightIcon && 'pr-10',
            error && 'border-red-300 focus:border-red-500 focus:ring-red-500',
            className
          )}
          {...props}
        />
        {rightIcon && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {rightIcon}
          </div>
        )}
      </div>

      {/* Error & Helper Text */}
      {error ? (
        <p className="text-xs text-red-600">{error}</p>
      ) : helperText ? (
        <p className="text-xs text-gray-500">{helperText}</p>
      ) : null}
    </div>
  );
}
